import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The design kit's type scale, which is named by ROLE rather than size (see
 * index.css). These are registered as font-size tokens via `--text-fh-*`.
 */
const FH_TEXT_SIZES = [
  "fh-meta",
  "fh-xs",
  "fh-th",
  "fh-body",
  "fh-cell",
  "fh-row",
  "fh-detail",
  "fh-panel-title",
  "fh-modal-title",
  "fh-page-title",
  "fh-stat",
] as const;

/**
 * tailwind-merge has to be told about the custom type scale.
 *
 * `text-*` is ambiguous — it is both font-size and text-colour. tailwind-merge
 * resolves that from its built-in list of known sizes, and a custom token like
 * `text-fh-cell` is not on it, so it guessed "colour" and treated the two as
 * conflicting. Whichever came second won and the other was silently dropped:
 *
 *   twMerge("text-white", "text-fh-cell")  ->  "text-fh-cell"   (white lost)
 *   twMerge("text-fh-cell", "text-white")  ->  "text-white"     (size lost)
 *
 * Which is why every primary button rendered dark-on-navy — Button applies its
 * variant colour before its size class — and why badges were losing their font size,
 * PillBadge applying them the other way round. Registering the tokens as font-size
 * makes both dimensions independent again.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: [...FH_TEXT_SIZES] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Seconds -> "7h 30m". Hubstaff reports in seconds; nobody reads in seconds. */
export function formatDuration(seconds: number): string {
  if (!seconds) return "0h";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (!hours) return `${minutes}m`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Currencies rendered with a symbol rather than a code.
 *
 * Only these three. It is not an oversight that PKR and AED are missing: the letter
 * renderer (api/src/letters/render.ts) draws with pdf-lib's standard Helvetica, which
 * is WinAnsi-encoded and THROWS on `₨` (U+20A8) and on Arabic script. `$`, `€` and `£`
 * are all in WinAnsi, so this map is exactly the set that can render identically in the
 * app and on a generated letter. Anything else falls back to its code, which is
 * unambiguous and always encodable.
 */
const CURRENCY_SYMBOLS: Record<string, string> = { USD: "$", EUR: "€", GBP: "£" };

export function formatMoney(amount: number | null | undefined, currency = "USD"): string {
  if (amount === null || amount === undefined) return "—";
  const symbol = CURRENCY_SYMBOLS[currency];
  // Sign in front of the symbol: "-$500", not "$-500". Deductions are negative.
  const sign = amount < 0 ? "-" : "";
  const magnitude = Math.abs(amount).toLocaleString("en-US", { maximumFractionDigits: 2 });
  return symbol ? `${sign}${symbol}${magnitude}` : `${sign}${currency} ${magnitude}`;
}

/** ISO date -> "20 Jul 2026". UTC so a date never shifts by a day near midnight. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(iso)}, ${date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

/**
 * Display an org reference by its real name, falling back to a humanised id.
 *
 * `humanise("cto")` gives "Cto", which is wrong and looks it. The title/department
 * records carry a proper `name` ("Chief Technology Officer"), so use that whenever the
 * collection has been loaded; the fallback only matters before it arrives or if the id
 * has been orphaned.
 */
export function orgLabel(
  id: string | null | undefined,
  nodes?: { id: string; name: string }[],
): string {
  if (!id) return "—";
  const match = nodes?.find((n) => n.id === id);
  return match ? match.name : humanise(id);
}

/**
 * Where someone works: which office, and whether they are in it.
 *
 * `locationId` is the office and `workLocation` is office/remote/hybrid, and they are
 * independent — a remote employee has no office. Interpolating both unconditionally gave
 * "— (Remote)", because orgLabel renders a missing id as an em dash.
 */
export function locationLabel(
  officeName: string,
  workLocation: string | null | undefined,
): string {
  const mode = workLocation ? humanise(workLocation) : "";
  const hasOffice = officeName && officeName !== "—";
  // "Remote (Remote)" when the location field has been used to record the work mode, which
  // happens because the two questions sound like one. Say it once.
  if (hasOffice && mode && officeName.toLowerCase() === mode.toLowerCase()) return mode;
  if (hasOffice && mode) return `${officeName} (${mode})`;
  if (hasOffice) return officeName;
  return mode || "—";
}

/** "employment-contract" -> "Employment contract". */
export function humanise(value: string | null | undefined): string {
  if (!value) return "—";
  const spaced = value.replace(/[-_]/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Today as YYYY-MM-DD, in UTC to match how the API stores dates. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The last calendar day of a YYYY-MM, as YYYY-MM-DD. Mirrors lastDayOfMonth in
 * api/src/lib/ids.ts — separate npm projects, kept in step by hand.
 *
 * Day 0 of the following month, so February and leap years come out right without a
 * table. UTC because these are plain calendar dates that must not shift by timezone.
 */
export function lastDayOfMonth(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return month;
  return new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);
}

export function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/** Inclusive day count, matching the API's countDays for full-day ranges. */
export function dayCount(startDate: string, endDate: string): number {
  if (!startDate || !endDate || endDate < startDate) return 0;
  return (
    Math.round(
      (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86_400_000,
    ) + 1
  );
}

/**
 * Routes that must work with no session at all.
 *
 * AuthProvider probes GET /api/auth/check on every route it is not told to skip, and a 401
 * there ends in a redirect to /login. That is right for the application and wrong for a page
 * whose whole point is that the visitor has no account — a candidate opening their offer link
 * was bounced to a sign-in screen they can never get past.
 *
 * Keep this list and the public routes in App.tsx in step: a public route missing from here is
 * not broken until someone signed out tries it.
 */
export function isPublicRoute(pathname: string): boolean {
  return pathname === "/login" || pathname.startsWith("/sign/");
}
